<template>
  <v-layout class="app-all">
    <HeaderToolbar
        :renderSideIcon="true"
        :renderRightMenuButtons="true"
        :fixed="true"
        :extended="true"
        :extensionHeight="'48px'"
      >
        <v-toolbar-title slot="header-extension" class="po-toolbar-title white--text ma-0">
          <v-layout align-center justify-start wrap>
            <v-flex
              xs7
              sm8
              class="text-truncate"
            >
              <v-btn
                icon
                @click="toggleLineList"
                :title="messages.PO_DETAIL_MENU_BUTTON_TITLE"
                small
              >
                <v-icon>view_list</v-icon>
              </v-btn>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <span
                    v-on="on"
                  >
                    {{ subTitleFormatted }}
                  </span>
                </template>
                <span>{{ subTitleFormatted }}</span>
              </v-tooltip>
            </v-flex>
            <v-flex xs5 sm4 class="text-xs-right">
              <PoLineItemActionsMenu
                :lineItemStatus="currentLineItemStatus"
                :imagesCount="imagesCount"
                :clients="clients"
                :isMyClient="isMyClient"
                :isMyEmployeeClient="isMyEmployeeClient"
                :downloadActionLoading="downloadingPdf"
                :downloadActionDisabled="disableDownloadAction"
                @po-line-items-download-approval="downloadApprovalWithConfirmation"
                @toggle-instructions="toggleInstructions"
              >
              </PoLineItemActionsMenu>
            </v-flex>
          </v-layout>
        </v-toolbar-title>
        <HeaderActions slot="actions" />
    </HeaderToolbar>
    <slot
      name="left"
      :showPoLineItemsList="showPoLineItemsList"
      :onListVisibilityChanged="onListVisibilityChanged"
    >
    </slot>
    <v-content class="po-content">
      <v-container pa-0 fluid fill-height>
        <slot></slot>
        <PORoleInstructions
          v-if="$isAllowed('view', 'Instructions')"
          :role="mainRole"
          :show="showInstructions"
          @visibility-changed="onInstructionsVisibilityChanged"
          :isShippingLabel="isShippingLabel"
        />
      </v-container>
      <SnackbarNotification />
    </v-content>
  </v-layout>
</template>

<script>

import SnackbarNotification from '@/components/common/SnackbarNotification.vue';
import HeaderToolbar from '@/components/common/HeaderToolbar.vue';
import HeaderActions from '@/components/common/HeaderActions.vue';
import PoLineItemActionsMenu from '@/components/PoDetails/PoLineItemActionsMenu.vue';
import PORoleInstructions from '@/components/PoDetails/SideBar/PORoleInstructions.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import PoDetailsConstants from '@/store/modules/poDetails/constants';
import SnackbarConstants from '@/store/modules/snackbarNotification/constants';
import Auth from '@/store/modules/auth/constants';
import PurchaseOrderLineStatus from '@/models/purchaseOrderLineStatus';

export default {
  name: 'PurchaseOrderBase',
  props: {
    isShippingLabel: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    SnackbarNotification,
    HeaderToolbar,
    PoLineItemActionsMenu,
    HeaderActions,
    PORoleInstructions,
  },
  created() {
    this.loadPoDetails(this.$route.params.purchaseOrderNumber);
  },
  data() {
    return {
      showPoLineItemsList: this.$vuetify.breakpoint.lgAndUp,
      showInstructions: this.$vuetify.breakpoint.lgAndUp,
      downloadingPdf: false,
      messages: {
        PO_DETAIL_MENU_BUTTON_TITLE: 'Show/Hide PO Line Items List',
      },
    };
  },
  computed: {
    ...mapState({
      loading: state => state.poDetails.loading,
      actionsDisabled: state => state.poDetails.actionsDisabled,
      clients: state => state.poDetails.clients,
      isMyClient: state => state.poDetails.isMyClient,
      isMyEmployeeClient: state => state.poDetails.isMyEmployeeClient,
      poNumber: state => state.poDetails.poNumber,
      currentLineInfoLoading: state => state.poDetails.approvedImagesLoading
                                    || state.poDetails.uploadedImagesLoading
                                    || state.poDetails.activityLoading,
    }),
    ...mapGetters('poDetails', {
      currentLineItemStatus: PoDetailsConstants.getterTypes.currentLineItemStatus,
      imagesCount: PoDetailsConstants.getterTypes.currentLineItemImagesCount,
      currentLineItem: PoDetailsConstants.getterTypes.currentLineItemInfo,
    }),
    ...mapGetters('auth', {
      mainRole: Auth.getterTypes.mainRole,
    }),
    subTitleFormatted() {
      return `${this.poNumberFormatted} ${this.clientFormatted}`;
    },
    poNumberFormatted() {
      return `PO #${this.poNumber}`;
    },
    clientFormatted() {
      return '';
    },
    currentUrl() {
      return this.$route.fullPath;
    },
    disableDownloadAction() {
      return this.currentLineItemStatus !== PurchaseOrderLineStatus.Approved.key
            || this.actionsDisabled;
    },
  },
  methods: {
    onInstructionsVisibilityChanged(show) {
      this.showInstructions = show;
    },
    toggleInstructions() {
      this.showInstructions = !this.showInstructions;
    },
    onListVisibilityChanged(show) {
      this.showPoLineItemsList = show;
    },
    toggleLineList() {
      this.showPoLineItemsList = !this.showPoLineItemsList;
    },
    getCurrentLineItemId() {
      return this.currentLineItem.lineItems[0].id;
    },
    downloadApprovalWithConfirmation(lineItemId) {
      this.$confirm('Every individual shipped box requires an approval document to be attached in order for the product to be received.'
      + '<br><br> Any box that does not have an approval document attached will be rejected.',
      'Printing Instructions',
      {
        color: 'primary',
        yesLabel: 'Print',
        noLabel: 'Cancel',
      })
        .then((payload) => {
          const { confirmed } = payload;

          if (confirmed) {
            this.downloadingPdf = true;
            this.downloadApproval(lineItemId);
          }
        });
    },
    downloadApproval(lineItemIds) {
      const self = this;

      this.getApprovalDocument({
        lineItemIds,
      })
        .then((response) => {
          this.$downloadBlob(response.pdfBlob, response.fileName);
        })
        .finally(() => {
          self.downloadingPdf = false;
        });
    },
    ...mapActions('poDetails', {
      loadPoDetails: PoDetailsConstants.actionTypes.loadPoDetails,
      getApprovalDocument: PoDetailsConstants.actionTypes.getApprovalDocument,
    }),
    ...mapActions('snackbarNotification', {
      successNotification: SnackbarConstants.actionTypes.success,
    }),
  },
};

</script>
